import {Component, computed, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {UserStateService} from '@services/frontend/user-state.service';
import {NotificationService} from '@services/frontend/notification.service';
// import {SubscriptionService} from '@services/subscription.service';
// import {SubscriptionStatus} from '@models/subscription.interface';
import {IUserSettings, IUserSettingsUpdate} from '@models/user.interface';
import {UserService} from '@services/user.service';
import {AuthService} from '@services/auth.service';

@Component({
  selector: 'app-expert-settings',
  imports: [FormsModule],
  templateUrl: './expert-settings.html',
})
export class ExpertSettings implements OnInit {
  private readonly userService = inject(UserService);
  private readonly userStateService = inject(UserStateService);
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  // private readonly subscriptionService = inject(SubscriptionService);
  private readonly router = inject(Router);

  private readonly passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  loading = signal(true);
  settings: WritableSignal<IUserSettings | null> = signal(null);
  showPasswordForm = signal(false);
  currentPassword = signal('');
  newPassword = signal('');
  confirmPassword = signal('');
  passwordSaving = signal(false);

  isNewPasswordValid = computed(() => this.passwordRegex.test(this.newPassword()));
  passwordsMatch = computed(() => this.newPassword() === this.confirmPassword());
  canSavePassword = computed(() =>
    this.currentPassword().length > 0 &&
    this.isNewPasswordValid() &&
    this.passwordsMatch() &&
    !this.passwordSaving()
  );
  // subscription = signal<SubscriptionStatus | null>(null);
  // subscriptionLoading = signal(true);
  // portalLoading = signal(false);

  ngOnInit(): void {
    // this.loadSubscription();
    this.userService.getMySettings().subscribe({
      next: (settings) => {
        this.settings.set(settings);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  saveContact(): void {
    const settings = this.settings();
    if (!settings) return;
    if (!settings.cabinetName?.trim()) {
      this.notificationService.error('Le nom du cabinet est obligatoire', 3000);
      return;
    }
    if (!settings.cabinetPhone?.trim()) {
      this.notificationService.error('Le téléphone du cabinet est obligatoire', 3000);
      return;
    }
    if (!settings.cabinetEmail?.trim() || !settings.cabinetEmail.includes('@')) {
      this.notificationService.error('L\'email du cabinet est obligatoire', 3000);
      return;
    }
    const payload: IUserSettingsUpdate = {
      contactName: settings.contactName,
      contactMail: settings.contactMail,
      cabinetName: settings.cabinetName?.trim() || null,
      cabinetPhone: settings.cabinetPhone?.trim() || null,
      cabinetEmail: settings.cabinetEmail?.trim() || null,
      legalAlertsEnabled: settings.legalAlertsEnabled,
      expiryAlertsEnabled: settings.expiryAlertsEnabled,
      emailNotificationsEnabled: settings.emailNotificationsEnabled,
      companyNotifications: settings.companyNotifications.map((entry) => ({siret: entry.siret, enabled: entry.enabled})),
    };
    this.userService.updateMySettings(payload).subscribe({
      next: (updated) => {
        this.settings.set(updated);
        this.notificationService.success('Paramètres enregistrés', 2000);
      },
      error: () => this.notificationService.error('Impossible d’enregistrer les paramètres', 3000),
    });
  }

  toggle(key: keyof Pick<IUserSettings, 'legalAlertsEnabled' | 'expiryAlertsEnabled' | 'emailNotificationsEnabled'>): void {
    this.settings.update((current) => current ? ({...current, [key]: !current[key]}) : current);
  }

  toggleCompanyNotification(siret: string): void {
    this.settings.update((current) => {
      if (!current) return current;
      return {
        ...current,
        companyNotifications: current.companyNotifications.map((entry) =>
          entry.siret === siret ? {...entry, enabled: !entry.enabled} : entry
        ),
      };
    });
  }

  /*
  loadSubscription(): void {
    this.subscriptionLoading.set(true);
    this.subscriptionService.checkSubscription().subscribe({
      next: (status) => {
        this.subscription.set(status);
        this.subscriptionLoading.set(false);
      },
      error: () => this.subscriptionLoading.set(false),
    });
  }

  getPlanLabel(): string {
    const sub = this.subscription();
    if (!sub?.subscribed || !sub.plan) return 'Wallet (Gratuit)';
    if (sub.plan === 'pro') return 'Pro';
    if (sub.plan === 'depot') return 'Dépôt';
    return sub.plan;
  }

  getSubscriptionEndDate(): string {
    const sub = this.subscription();
    if (!sub?.subscriptionEnd) return '';
    return new Date(sub.subscriptionEnd).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  }

  openCustomerPortal(): void {
    this.portalLoading.set(true);
    this.subscriptionService.openCustomerPortal().subscribe({
      next: (res) => {
        this.portalLoading.set(false);
        if (res.url) {
          window.open(res.url, '_blank');
        }
      },
      error: () => {
        this.portalLoading.set(false);
        this.notificationService.error('Impossible d\'ouvrir le portail de gestion', 3000);
      },
    });
  }
  */

  togglePasswordForm(): void {
    this.showPasswordForm.update((v) => !v);
    if (!this.showPasswordForm()) {
      this.resetPasswordForm();
    }
  }

  savePassword(): void {
    if (!this.canSavePassword()) {
      return;
    }
    this.passwordSaving.set(true);
    this.authService.changeMyPassword({
      currentPassword: this.currentPassword(),
      newPassword: this.newPassword(),
    }).subscribe({
      next: () => {
        this.passwordSaving.set(false);
        this.notificationService.success('Mot de passe modifié', 2500);
        this.resetPasswordForm();
        this.showPasswordForm.set(false);
      },
      error: () => {
        this.passwordSaving.set(false);
        this.notificationService.error('Mot de passe actuel incorrect ou erreur serveur', 3500);
      },
    });
  }

  private resetPasswordForm(): void {
    this.currentPassword.set('');
    this.newPassword.set('');
    this.confirmPassword.set('');
  }

  logout(): void {
    this.userStateService.deleteToken();
    void this.router.navigate(['/login']);
  }
}
